/**
 * SolarNEST max wind (mph) and max snow (psf) by panel layout, panel dimension, and tilt option.
 * Used for defaults when Dlubal is unavailable and for above-max warning.
 */

import { StudioSystemType } from 'types/global'

/** SolarNest required tilt in degrees for both relative tilt (E/W) and roll (N/S / triple tilt). */
export const SOLARNEST_REQUIRED_TILT_DEGREES = 8

/** Default ground clearance in inches when SolarNest tilt option changes (East/West or Triple). */
export const SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES = 18

const M_TO_IN = 39.3701
const KG_TO_LBS = 2.20462

export type LayoutBucket = '2x4' | '2x6_2x16'
export type DimensionBucket = 'dim1' | 'dim2' | 'dim3'

export interface MaxWindSnow {
  maxWindMph: number
  maxSnowPsf: number
}

// Table: Double tilt (East/West) max loads by layout and dimension (from spec)
const DOUBLE_TILT_TABLE: Record<LayoutBucket, Record<DimensionBucket, MaxWindSnow>> = {
  '2x4': {
    dim1: { maxWindMph: 140, maxSnowPsf: 57 },
    dim2: { maxWindMph: 133, maxSnowPsf: 26 },
    dim3: { maxWindMph: 120, maxSnowPsf: 16 },
  },
  '2x6_2x16': {
    dim1: { maxWindMph: 140, maxSnowPsf: 52 },
    dim2: { maxWindMph: 120, maxSnowPsf: 15 },
    dim3: { maxWindMph: 120, maxSnowPsf: 15 },
  },
}

// Table: Triple tilt max loads by layout and dimension (from spec)
const TRIPLE_TILT_TABLE: Record<LayoutBucket, Record<DimensionBucket, MaxWindSnow>> = {
  '2x4': {
    dim1: { maxWindMph: 140, maxSnowPsf: 55 },
    dim2: { maxWindMph: 114, maxSnowPsf: 16 },
    dim3: { maxWindMph: 102, maxSnowPsf: 10 },
  },
  '2x6_2x16': {
    dim1: { maxWindMph: 140, maxSnowPsf: 48 },
    dim2: { maxWindMph: 102, maxSnowPsf: 10 },
    dim3: { maxWindMph: 102, maxSnowPsf: 10 },
  },
}

const MOST_CONSERVATIVE: MaxWindSnow = { maxWindMph: 102, maxSnowPsf: 10 }

// Reference dimensions (inches) and weight (lbs) for bucket matching
const DIM_SPECS: { bucket: DimensionBucket; weightLbs: number; lengthIn: number; widthIn: number }[] = [
  { bucket: 'dim1', weightLbs: 47, lengthIn: 67.8, widthIn: 44.65 },
  { bucket: 'dim2', weightLbs: 61, lengthIn: 89.7, widthIn: 44.65 },
  { bucket: 'dim3', weightLbs: 87, lengthIn: 93.9, widthIn: 51.3 },
]

function getPanelLayoutDimensionsFromCells(cellsActive: string[]): { numColumns: number; numRows: number } {
  if (!cellsActive?.length) return { numColumns: 0, numRows: 0 }
  const panels = cellsActive.map((cell) => {
    const coords = cell.split(',')
    const column = Number(coords[0]) * -1
    const row = Number(coords[1])
    return { row, column }
  })
  const smallestColumnCoord = Math.min(...panels.map((p) => p.column))
  if (smallestColumnCoord < 0) panels.forEach((p) => (p.column += Math.abs(smallestColumnCoord)))
  const smallestRowCoord = Math.min(...panels.map((p) => p.row))
  if (smallestRowCoord < 0) panels.forEach((p) => (p.row += Math.abs(smallestRowCoord)))
  let numColumns = 0
  let numRows = 0
  panels.forEach(({ row, column }) => {
    if (row + 1 > numRows) numRows = row + 1
    if (column + 1 > numColumns) numColumns = column + 1
  })
  return { numColumns, numRows }
}

export function getLayoutBucket(numColumns: number, numRows: number): LayoutBucket | null {
  if (numColumns === 2 && numRows === 4) return '2x4'
  if (numRows >= 6 && numRows <= 16 && numColumns === 2) return '2x6_2x16'
  return null
}

export function getDimensionBucket(weightLbs: number, lengthIn: number, widthIn: number): DimensionBucket {
  for (const spec of DIM_SPECS) {
    if (weightLbs <= spec.weightLbs && lengthIn <= spec.lengthIn && widthIn <= spec.widthIn) {
      return spec.bucket
    }
  }
  return 'dim3'
}

/**
 * Get max wind (mph) and max snow (psf) for the current system layout and panel dimension.
 * Returns null if system has no grids, no module type, or layout not in table (e.g. not 2x4 or 2x6-2x12).
 * Selects Double tilt or Triple tilt table based on the active tilt option.
 */
export function getSolarNestMaxWindSnow(system: StudioSystemType | null | undefined): MaxWindSnow | null {
  if (!system?.moduleGrids?.().length) return null
  const tiltOptionRooftop = system.integration_json?.solarNestRooftop?.tiltOption
  const tiltOptionGroundMount = system.integration_json?.solarNestGroundMount?.tiltOption
  const tiltOption = tiltOptionRooftop || tiltOptionGroundMount
  if (tiltOption !== 'eastWestTilt' && tiltOption !== 'tripleTilt') return null

  const grids = system.moduleGrids()
  const firstGrid = grids[0]
  if (!firstGrid?.cellsActive?.length) return null
  const moduleType = firstGrid.moduleType?.()
  if (!moduleType) return null

  const { numColumns, numRows } = getPanelLayoutDimensionsFromCells(firstGrid.cellsActive)
  const layoutBucket = getLayoutBucket(numColumns, numRows)
  if (!layoutBucket) return null

  const widthM = moduleType.width ?? 0
  const heightM = moduleType.height ?? 0
  const lengthIn = Math.max(widthM, heightM) * M_TO_IN
  const widthIn = Math.min(widthM, heightM) * M_TO_IN
  const weightKg = (moduleType as { weight?: number }).weight
  const weightLbs = weightKg != null ? weightKg * KG_TO_LBS : 50

  const dimBucket = getDimensionBucket(weightLbs, lengthIn, widthIn)
  const table = tiltOption === 'tripleTilt' ? TRIPLE_TILT_TABLE : DOUBLE_TILT_TABLE
  const maxes = table[layoutBucket][dimBucket]
  return maxes ?? MOST_CONSERVATIVE
}
